import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';

interface SwrCacheDB extends DBSchema {
  cache: {
    key: string;
    value: {
      key: string;
      data: string; // serialized SWR cache entry
      timestamp: number;
    };
  };
}

const DB_NAME = 'macrodash-cache';
const STORE_NAME = 'cache';

let dbPromise: Promise<IDBPDatabase<SwrCacheDB>> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<SwrCacheDB>(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

export const swrCacheProvider = {
  async get(key: string) {
    const db = await getDB();
    const entry = await db.get(STORE_NAME, key);
    if (!entry) return undefined;

    // Check if cache is stale (24 hours)
    const STALE_TIME = 1000 * 60 * 60 * 24;
    if (Date.now() - entry.timestamp > STALE_TIME) {
      await db.delete(STORE_NAME, key);
      return undefined;
    }

    return JSON.parse(entry.data);
  },

  async set(key: string, value: unknown) {
    const db = await getDB();
    await db.put(STORE_NAME, {
      key,
      data: JSON.stringify(value),
      timestamp: Date.now(),
    });
  },

  async delete(key: string) {
    const db = await getDB();
    await db.delete(STORE_NAME, key);
  },

  async clear() {
    const db = await getDB();
    await db.clear(STORE_NAME);
  },
};
